import React from 'react';
import { StyleProp, StyleSheet, TouchableOpacity, ViewStyle } from 'react-native';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import Anchor from '@/assets/icons/anchor';
import DiscountIcon from '@/assets/icons/discount';
import { FreePickIcon } from '@/assets/icons/free-pick-icon';
import { InflatedIcon } from '@/assets/icons/inflated';
import { NukeIcon } from '@/assets/icons/nuke-icon';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { usePickTypesEducation } from '@/feature/educational-picks/components/PickTypesEducationModalProvider';
import { PickTypesEducationTarget } from '@/feature/educational-picks/utils/pick-types-education-target';
import { common } from '@/styles/styles';
import { ProjectionType } from '@/types/api.generated';
import { getNonRegularProjections } from '@/utils/getProjectionsTypes';

type GroupProjectionsIconsProps = {
    projections?: PlayerProjectionFragment[];
    triggerEducationalSheet?: boolean;
};

type ProjectionIcon = {
    key: string;
    isVisible: boolean;
    backgroundColor: React.ComponentProps<typeof Box>['backgroundColor'];
    /** Extra style stacked on top of the shared wrapper/border styles (e.g. zIndex). */
    style?: StyleProp<ViewStyle>;
    icon: React.ReactNode;
    educationTarget: PickTypesEducationTarget;
};

/* Display the projection icons for all projections */
export const GroupProjectionsIcons = ({ projections, triggerEducationalSheet }: GroupProjectionsIconsProps) => {
    const {
        hasSpecialIncreasedProjections,
        hasSpecialDecreasedProjections,
        hasAnchorProjections,
        hasNukeProjections,
        hasFreePickProjections,
    } = getNonRegularProjections(projections);
    const { showPickTypesEducationModal } = usePickTypesEducation();

    // Ordered by stacking priority. Icons overlap left-to-right, so every icon
    // except the first visible one is nudged left — hence the `index > 0` rule below.
    const icons: ProjectionIcon[] = [
        {
            key: 'specialDecreased',
            isVisible: hasSpecialDecreasedProjections,
            backgroundColor: ds.colorToken.background.pickType.discount.strong,
            style: styles.iconSpecialDiscountWrapper,
            icon: <DiscountIcon width={16} height={16} />,
            educationTarget: { projectionType: ProjectionType.Special, specialIncreased: false },
        },
        {
            key: 'specialIncreased',
            isVisible: hasSpecialIncreasedProjections,
            backgroundColor: ds.colorToken.background.pickType.surge.strong,
            style: styles.iconSpecialIncreaseWrapper,
            icon: <InflatedIcon />,
            educationTarget: { projectionType: ProjectionType.Special, specialIncreased: true },
        },
        {
            key: 'freePick',
            isVisible: hasFreePickProjections,
            backgroundColor: ds.colorToken.background.pickType.discount.strong,
            style: styles.iconFreePickWrapper,
            icon: <FreePickIcon width={16} height={16} />,
            educationTarget: { projectionType: ProjectionType.FreePick },
        },
        {
            key: 'nuke',
            isVisible: hasNukeProjections,
            backgroundColor: ds.colorToken.background.pickType.boost.strong,
            style: styles.iconNuke,
            icon: <NukeIcon width={16} height={16} color={ds.colorValue.icon.inverse} />,
            educationTarget: { projectionType: ProjectionType.Nuke },
        },
        {
            key: 'anchor',
            isVisible: hasAnchorProjections,
            backgroundColor: ds.colorToken.background.neutral.inverse,
            icon: <Anchor width={16} height={16} />,
            educationTarget: { projectionType: ProjectionType.Anchor },
        },
    ];

    const visibleIcons = icons.filter(icon => icon.isVisible);

    if (visibleIcons.length === 0) {
        return null;
    }

    // open the education modal on the tab of the top-priority (first stacked) icon
    const onPress = () => showPickTypesEducationModal(visibleIcons[0].educationTarget);

    return (
        <TouchableOpacity
            onPress={triggerEducationalSheet ? onPress : undefined}
            disabled={!triggerEducationalSheet}
            style={styles.tapTargetStyle}
            activeOpacity={1}
        >
            <Row alignItems="center" marginRight={'s4'}>
                {visibleIcons.map(({ key, backgroundColor, style, icon }, index) => (
                    <Box
                        key={key}
                        backgroundColor={backgroundColor}
                        padding={'s2'}
                        justifyContent={'center'}
                        style={[styles.iconWrapper, styles.border, style, index > 0 && styles.leftIcon]}
                    >
                        {icon}
                    </Box>
                ))}
            </Row>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    iconWrapper: {
        borderCurve: 'continuous',
    },
    iconSpecialDiscountWrapper: {
        ...common.zIndex5,
        elevation: 5,
    },
    iconSpecialIncreaseWrapper: {
        ...common.zIndex4,
        elevation: 4,
    },
    iconFreePickWrapper: {
        ...common.zIndex3,
        elevation: 3,
    },
    leftIcon: {
        marginLeft: -6,
    },
    iconNuke: {
        ...common.zIndex2,
        elevation: 2,
    },
    border: {
        borderWidth: 2,
        borderColor: ds.colorValue.background.neutral.base, // not an actual border, but a way to create separation between icons
        borderRadius: 8,
        overflow: 'hidden',
    },
    tapTargetStyle: {
        zIndex: 10,
    },
});
